// Utilities for client-side (near-)lossless image handling and base64 conversion
// Notes:
// - We do not resize images; dimensions are preserved
// - We attempt a lossless re-encode (PNG/WebP) to strip metadata and reduce size when possible
// - If re-encoding is not smaller, we fall back to original

export const readFileAsDataURL = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const dataURLToRawBase64 = (dataURL) => {
  if (typeof dataURL !== 'string') return '';
  const commaIdx = dataURL.indexOf(',');
  return commaIdx >= 0 ? dataURL.slice(commaIdx + 1) : dataURL;
};

 const isHeicLikeFile = (file) => {
   const type = String(file?.type || '').toLowerCase();
   const name = String(file?.name || '').toLowerCase();
   return type === 'image/heic' || type === 'image/heif' || name.endsWith('.heic') || name.endsWith('.heif');
 };

 export const convertHeicToJpegIfNeeded = async (file) => {
   if (!file || !isHeicLikeFile(file)) return file;
   try {
     const mod = await import('heic2any');
     const heic2any = mod?.default ?? mod;
     const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
     const blob = Array.isArray(converted) ? converted[0] : converted;
     const baseName = String(file.name || 'image').replace(/\.(heic|heif)$/i, '');
     return new File([blob], `${baseName}.jpg`, {
       type: 'image/jpeg',
       lastModified: typeof file.lastModified === 'number' ? file.lastModified : Date.now(),
     });
   } catch {
     return file;
   }
 };

const supportsWebp = (() => {
  try {
    const canvas = document.createElement('canvas');
    const data = canvas.toDataURL('image/webp');
    return data.indexOf('data:image/webp') === 0;
  } catch {
    return false;
  }
})();

const loadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

const reencodeImageDataURL = async (dataURL, mimeType) => {
  const img = await loadImage(dataURL);
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0);
  // Try toBlob when available for better memory usage
  if (canvas.toBlob) {
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, mimeType));
    const reReader = new FileReader();
    const dataUrl = await new Promise((resolve) => {
      reReader.onload = () => resolve(reReader.result);
      reReader.readAsDataURL(blob);
    });
    return dataUrl;
  }
  // Fallback to dataURL
  return canvas.toDataURL(mimeType);
};

export const fileToRawBase64Lossless = async (file) => {
  // Read original
  const originalDataURL = await readFileAsDataURL(file);
  const originalRaw = dataURLToRawBase64(originalDataURL);

  // Decide re-encode target
  // Prefer WebP for non-JPEG if supported, else PNG
  const isJpeg = /image\/jpe?g/i.test(file.type);
  const targetMime = supportsWebp && !isJpeg ? 'image/webp' : 'image/png';

  try {
    const reDataURL = await reencodeImageDataURL(originalDataURL, targetMime);
    const reRaw = dataURLToRawBase64(reDataURL);
    // Choose smaller payload
    return reRaw.length < originalRaw.length ? reRaw : originalRaw;
  } catch {
    // If re-encode fails, use original
    return originalRaw;
  }
};

// Max pixel dimension — caps decode/canvas memory for large phone photos (12MP+).
const MAX_DIM = 2048;

// Aggressive lossy WebP compression with downscale.
// Returns a File (WebP) that is typically 5–10× smaller than the original JPEG/PNG.
// Falls back to the original blob if the browser doesn't support WebP or compression fails.
export const compressToWebp = async (blob, quality = 0.3) => {
  if (!supportsWebp) return blob;
  try {
    const url = URL.createObjectURL(blob);
    const img = await loadImage(url);
    URL.revokeObjectURL(url);

    const scale = Math.min(1, MAX_DIM / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const outW = canvas.width;
    const outH = canvas.height;

    const webpBlob = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/webp', quality)
    );

    // Eagerly free canvas memory
    canvas.width = 0;
    canvas.height = 0;

    if (!webpBlob || webpBlob.size === 0) return blob;

    const oldKB = (blob.size / 1024).toFixed(1);
    const newKB = (webpBlob.size / 1024).toFixed(1);
    const pct = ((1 - webpBlob.size / blob.size) * 100).toFixed(0);
    const dimInfo = scale < 1 ? ` scaled ${img.naturalWidth}×${img.naturalHeight} → ${outW}×${outH}` : '';
    console.log(`[compressToWebp] ${blob.name || 'image'}: ${oldKB}KB → ${newKB}KB (−${pct}%)${dimInfo}`);

    const baseName = (blob.name || 'image').replace(/\.[^.]+$/, '');
    return new File([webpBlob], `${baseName}.webp`, {
      type: 'image/webp',
      lastModified: Date.now(),
    });
  } catch {
    return blob;
  }
};

// Date/time shown on the location+timestamp overlay, matching the Events/Breakdown photos.
export const formatOverlayDateTime = (date = new Date()) => {
  const d = date.toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: '2-digit' });
  const t = date.toLocaleTimeString('en-ZA', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  return `${d} ${t}`;
};

/**
 * Burns a location + timestamp overlay onto a photo — the same tag the Events and
 * Breakdown images carry, restored for the new Inspections capture. The caller
 * supplies the coordinates (fetched once per batch) and an optional timestamp.
 * The Lat/Lng lines are shown only when a real fix is available, so a denied or
 * unavailable location still gets a timestamp. Returns a new File; on any failure
 * the original file is returned unchanged.
 */
export const stampLocationTime = async (imageFile, { latitude, longitude, dateTime } = {}) => {
  try {
    const stamp = dateTime || formatOverlayDateTime();
    const hasFix =
      Number.isFinite(latitude) &&
      Number.isFinite(longitude) &&
      !(Number(latitude) === 0 && Number(longitude) === 0);
    const imageUrl = URL.createObjectURL(imageFile);

    return await new Promise((resolve) => {
      const image = new Image();

      image.onload = () => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        canvas.width = image.naturalWidth || image.width;
        canvas.height = image.naturalHeight || image.height;
        context.drawImage(image, 0, 0);

        const fontSize = Math.max(18, Math.floor(canvas.width / 42));
        const lines = [stamp];
        if (hasFix) {
          lines.push(`Lat: ${Number(latitude).toFixed(6)}`);
          lines.push(`Lng: ${Number(longitude).toFixed(6)}`);
        }
        const padding = Math.max(12, Math.floor(canvas.width / 80));
        const lineHeight = fontSize + 8;
        const boxHeight = lineHeight * lines.length + padding;

        // Set the font before measuring so the box only spans the text, not the
        // full image width. Clamp so it never runs wider than the image.
        context.font = `${fontSize}px Arial`;
        const textWidth = Math.max(...lines.map((line) => context.measureText(line).width));
        const boxWidth = Math.min(canvas.width, textWidth + padding * 2);

        context.fillStyle = 'rgba(0, 0, 0, 0.55)';
        context.fillRect(0, canvas.height - boxHeight, boxWidth, boxHeight);
        context.fillStyle = '#fff';
        lines.forEach((line, index) => {
          context.fillText(line, padding, canvas.height - boxHeight + padding + fontSize + index * lineHeight);
        });

        const type = /^image\//.test(imageFile.type || '') ? imageFile.type : 'image/jpeg';
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(imageUrl);
            canvas.width = 0;
            canvas.height = 0;
            if (!blob) {
              resolve(imageFile);
              return;
            }
            resolve(new File([blob], imageFile.name || 'image.jpg', { type, lastModified: Date.now() }));
          },
          type,
          0.92
        );
      };

      image.onerror = () => {
        URL.revokeObjectURL(imageUrl);
        resolve(imageFile);
      };

      image.src = imageUrl;
    });
  } catch {
    return imageFile;
  }
};

export const filesToImagePayload = async (files, fieldKey) => {
  if (!files || files.length === 0) return [];
  const tasks = files.map(async (f) => {
    try {
      const normalizedFile = await convertHeicToJpegIfNeeded(f);
      // Fast path: small files or JPEGs use original base64 (no re-encode)
      const isJpeg = /image\/jpe?g/i.test(normalizedFile.type);
      const isSmall = typeof normalizedFile.size === 'number' && normalizedFile.size < 400 * 1024; // < 400KB
      if (isJpeg || isSmall) {
        const dataUrl = await readFileAsDataURL(normalizedFile);
        const raw = dataURLToRawBase64(dataUrl);
        return { fieldKey, base64: raw };
      }

      // Otherwise, attempt lossless re-encode and choose smaller payload
      const base64 = await fileToRawBase64Lossless(normalizedFile);
      return { fieldKey, base64 };
    } catch {
      // Fallback to original if anything fails
      try {
        const dataUrl = await readFileAsDataURL(f);
        const raw = dataURLToRawBase64(dataUrl);
        return { fieldKey, base64: raw };
      } catch {
        return null;
      }
    }
  });
  const results = await Promise.all(tasks);
  return results.filter(Boolean);
};
